import { useMemo, useState } from 'react'
import { useFiscalYear } from '@/lib/fiscalYear'
import { Link } from 'react-router-dom'
import {
  findDuplicateCharges,
  getFiscalYearLabel,
} from '@budgetmonkey/shared'
import { Badge, Button, Card, ErrorNote, Money, Select } from '@/components/ui'
import { formatDate } from '@/lib/dates'
import { humanErrorMessage } from '@/lib/errorMessage'
import { useBudgetCodes, useCodesById } from '@/features/budgetCodes/hooks'
import { TransactionForm } from './TransactionForm'
import { TransactionList } from './TransactionList'
import { TransactionDetail } from './TransactionDetail'
import { useCreateTransaction, useMergeCharges, useTransactions } from './hooks'
import { usePeriods } from '@/features/reconciliation/hooks'
import { loadStateOf, LoadingOrOffline } from '@/lib/loadState'

type Mode =
  | { kind: 'list' }
  | { kind: 'new' }
  // ID rather than the object, so the detail view reads live from the cache.
  | { kind: 'detail'; transactionId: string }

export function TransactionsScreen() {
  const fiscalYear = useFiscalYear()
  const [mode, setMode] = useState<Mode>({ kind: 'list' })
  const [codeFilter, setCodeFilter] = useState('')
  const [monthFilter, setMonthFilter] = useState('')

  const transactionsQuery = useTransactions(fiscalYear)
  const { data: transactions = [], error } = transactionsQuery
  const state = loadStateOf(transactionsQuery)
  const { data: codes = [] } = useBudgetCodes(fiscalYear)
  // No year passed: a charge files itself by its purchase date, which is not
  // necessarily the year being looked at.
  const create = useCreateTransaction()
  const merge = useMergeCharges(fiscalYear)
  const [confirmingMerge, setConfirmingMerge] = useState<string>()

  const { data: periods = [] } = usePeriods()

  /**
   * Charges on a packet that has already gone to the business office.
   *
   * A merge touching one is refused, so proposing it would be an offer that
   * can only fail — CLAUDE.md's rule is that the screen withholds the action
   * with its reason rather than erroring on the press. The refusal in
   * `mergeCharges` stays regardless: this is the courtesy, that is the gate.
   */
  const submittedChargeIds = useMemo(
    () =>
      new Set(
        periods
          .filter((period) => period.status === 'submitted')
          .flatMap((period) => period.orderedTransactionIds),
      ),
    [periods],
  )

  /**
   * Charges that look like one purchase — a fare beside the same fare plus
   * its tip, or two rows sharing a document. Nothing merges without being
   * asked; this only puts the question where it can be seen.
   */
  const duplicates = useMemo(
    () =>
      findDuplicateCharges(transactions).filter(
        ({ keep, remove }) =>
          !submittedChargeIds.has(keep.id ?? '') && !submittedChargeIds.has(remove.id ?? ''),
      ),
    [transactions, submittedChargeIds],
  )

  // Resolved across years, not just the one on screen: a charge moved to
  // last year still points at the code document it was filed against.
  const codesById = useCodesById()

  /**
   * The months that actually have transactions, newest first.
   *
   * Built from the data rather than listing all twelve: a fiscal year that is
   * two months old should not offer ten empty months to filter by.
   */
  const months = useMemo(() => {
    const seen = new Map<string, string>()
    for (const transaction of transactions) {
      const key = transaction.transactionDate.slice(0, 7)
      if (!seen.has(key)) {
        seen.set(
          key,
          new Date(`${key}-01T00:00:00Z`).toLocaleDateString('en-US', {
            month: 'long',
            year: 'numeric',
            timeZone: 'UTC',
          }),
        )
      }
    }
    return [...seen.entries()]
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([value, label]) => ({ value, label }))
  }, [transactions])

  const filtered = useMemo(
    () =>
      transactions.filter(
        (transaction) =>
          (codeFilter === '' || transaction.budgetCodeId === codeFilter) &&
          (monthFilter === '' || transaction.transactionDate.startsWith(monthFilter)),
      ),
    [transactions, codeFilter, monthFilter],
  )

  if (mode.kind === 'new') {
    return (
      <Section title="Add transaction" fiscalYear={fiscalYear}>
        <Card>
          <TransactionForm
            submitLabel="Add transaction"
            fiscalYear={fiscalYear}
            pending={create.isPending}
            error={create.error?.message}
            onCancel={() => setMode({ kind: 'list' })}
            onSubmit={(values) =>
              create.mutate(values, { onSuccess: () => setMode({ kind: 'list' }) })
            }
          />
        </Card>
      </Section>
    )
  }

  if (mode.kind === 'detail') {
    return (
      <TransactionDetail
        transactionId={mode.transactionId}
        codesById={codesById}
        fiscalYear={fiscalYear}
        onClose={() => setMode({ kind: 'list' })}
      />
    )
  }

  function clearFilters() {
    setCodeFilter('')
    setMonthFilter('')
  }

  return (
    <Section title="Transactions" fiscalYear={fiscalYear}>
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="primary" onClick={() => setMode({ kind: 'new' })}>
          Add transaction
        </Button>

        {transactions.length > 0 && (
          <span className="text-sm text-slate-500">
            {filtered.length === transactions.length
              ? `${transactions.length} this fiscal year`
              : `${filtered.length} of ${transactions.length}`}
          </span>
        )}
      </div>

      {error != null && (
        <ErrorNote>{humanErrorMessage(error, "Couldn't load your charges.")}</ErrorNote>
      )}

      {duplicates.length > 0 && (
        <Card className="space-y-3">
          <div>
            <h2 className="font-medium text-slate-900">
              {duplicates.length === 1
                ? 'One pair looks like a single purchase'
                : `${duplicates.length} pairs look like a single purchase`}
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              A meal or a ride billed twice — the total, and the total with the
              tip. Merging keeps the larger amount, which is what the card was
              charged, and moves every photo onto it. Nothing is lost.
            </p>
          </div>

          <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
            {duplicates.map(({ keep, remove, reason }) => {
              const pairId = `${keep.id}:${remove.id}`
              return (
                <li key={pairId} className="space-y-2 px-4 py-3">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="font-medium text-slate-900">{keep.vendor}</span>
                    <span className="text-sm text-slate-500">
                      {formatDate(keep.transactionDate)}
                    </span>
                    <Badge tone="warn">
                      {reason === 'shared-receipt'
                        ? 'Same photo on both'
                        : 'Differs by a tip'}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600">
                    Keep <Money value={keep.amount} className="font-medium" /> and
                    fold in <Money value={remove.amount} /> — {remove.description || 'no description'}.
                  </p>
                  {confirmingMerge === pairId ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        variant="primary"
                        disabled={merge.isPending}
                        onClick={() =>
                          merge.mutate(
                            { keepId: keep.id ?? '', removeId: remove.id ?? '' },
                            { onSuccess: () => setConfirmingMerge(undefined) },
                          )
                        }
                      >
                        {merge.isPending ? 'Merging…' : 'Yes — merge them'}
                      </Button>
                      <Button variant="ghost" onClick={() => setConfirmingMerge(undefined)}>
                        Keep both
                      </Button>
                    </div>
                  ) : (
                    <Button variant="ghost" onClick={() => setConfirmingMerge(pairId)}>
                      These are one purchase
                    </Button>
                  )}
                </li>
              )
            })}
          </ul>

          {merge.error != null && (
            <ErrorNote>{humanErrorMessage(merge.error, "Couldn't merge those charges.")}</ErrorNote>
          )}
        </Card>
      )}

      {state !== 'ready' ? (
        <LoadingOrOffline state={state} what="Your charges" />
      ) : (
        <TransactionList
          transactions={filtered}
          codesById={codesById}
          filtersActive={codeFilter !== '' || monthFilter !== ''}
          filters={
            <>
              <Select
                value={codeFilter}
                onChange={(event) => setCodeFilter(event.target.value)}
                aria-label="Filter by budget code"
                className="w-auto"
              >
                <option value="">All budget codes</option>
                {codes.map((code) => (
                  <option key={code.id} value={code.id}>
                    {code.shortLabel}
                  </option>
                ))}
              </Select>
              <Select
                value={monthFilter}
                onChange={(event) => setMonthFilter(event.target.value)}
                aria-label="Filter by month"
                className="w-auto"
              >
                <option value="">All months</option>
                {months.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </Select>
              {(codeFilter !== '' || monthFilter !== '') && (
                <Button variant="ghost" onClick={clearFilters}>
                  Clear
                </Button>
              )}
            </>
          }
          onSelect={(transaction) =>
            setMode({ kind: 'detail', transactionId: transaction.id ?? '' })
          }
          emptyTitle={`No transactions for ${getFiscalYearLabel(fiscalYear)} yet`}
          emptyBody={
            codes.length === 0 ? (
              <p>
                Add your{' '}
                <Link to="/setup" className="underline underline-offset-4">
                  budget codes
                </Link>{' '}
                first.
              </p>
            ) : (
              <p>
                Photograph a receipt or forward one by email and file it from{' '}
                <Link to="/receipts" className="underline underline-offset-4">
                  Receipts
                </Link>
                , import a cycle from{' '}
                <Link to="/import" className="underline underline-offset-4">
                  CSIU
                </Link>
                , or add one by hand below.
              </p>
            )
          }
        />
      )}
    </Section>
  )
}

function Section({
  title,
  fiscalYear,
  children,
}: {
  title: string
  fiscalYear: number
  children: React.ReactNode
}) {
  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        <p className="text-sm text-slate-500">{getFiscalYearLabel(fiscalYear)}</p>
      </div>
      {children}
    </div>
  )
}
